'use client';

import {useAtom} from 'jotai';
import Image from 'next/image';
import {useEffect} from 'react';
import {usePreventScroll} from 'react-aria';
import type {Map as MapboxMap} from 'mapbox-gl';
import {AnimatePresence, m as motion} from 'motion/react';
import {arrowRightLine, bucket, checkLine, closeLine, starFill} from 'src/utils/icons';
import SavePlaceButtonWrapper from '../save-place/SavePlaceButtonWrapper';
import RatePlaceButtonWrapper from '../rate-place/RatePlaceButtonWrapper';
import {getShadowColorFromBlur} from 'src/utils/getColorFromBlur';
import type {MyList} from 'server/apiHandlers/getMyListsHandler';
import MapPlaceCardEmptyState from './MapPlaceCardEmptyState';
import formatPlaceType from 'src/utils/formatPlaceType';
import getImageAltText from 'src/utils/getImageAltText';
import useMapSelectedPlace from './useMapSelectedPlace';
import {modalTransition} from '../ResponsiveModal';
import getImageUrl from 'src/utils/getImageUrl';
import SmallLoader from '../common/SmallLoader';
import {mapLoadingPlaceAtom} from './useMap';
import SmallRating from '../SmallRating';
import BaseButton from '../BaseButton';
import RemixIcon from '../RemixIcon';

type Props = {
  places: Partial<Place>[];
  reviews?: Review[];
  myLists: MyList[];
  session: IronSession | null;
  map: MapboxMap | null;
};

const MapPlaceCard = ({places, myLists, reviews, session, map}: Props) => {
  const {place, unSelectPlace} = useMapSelectedPlace({places, map});
  const [loadingPlace, setLoadingPlace] = useAtom(mapLoadingPlaceAtom);

  const visitedList = myLists.find((list) => list.nameId === 'visited-places');
  const isRatedByMe = visitedList ? visitedList.places.some((p) => p === place?._id) : false;
  const isPlaceSaved =
    place && myLists.some((list) => list.nameId !== 'visited-places' && list.places.includes(place._id));

  const userReview = place && reviews?.find((review) => review.place === place._id);

  const shouldShowCard = place || loadingPlace;

  usePreventScroll({isDisabled: !shouldShowCard});

  useEffect(() => {
    return () => setLoadingPlace(null);
  }, []);

  return (
    <AnimatePresence>
      {shouldShowCard ? (
        <motion.div
          initial={{opacity: 0, scale: 0.9}}
          animate={{opacity: 1, scale: 1}}
          exit={{opacity: 0, scale: 0.95}}
          style={{originY: '100%'}}
          transition={modalTransition}
          className="absolute max-[800px]:fixed z-3 -bottom-2 max-[800px]:bottom-3 bg-[rgb(255_255_255_/_0.85)] dark:bg-[rgb(22_15_25_/_0.85)] backdrop-blur-lg backdrop-saturate-150 w-[420px] max-[800px]:w-[calc(100vw-20px)] min-h-[100px] shadow-big-card p-4 rounded-3xl overflow-hidden"
        >
          {place && !loadingPlace ? (
            <motion.div initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}}>
              <div className="flex gap-5 items-start">
                {place.images?.[0] ? (
                  <div className="relative w-[56px] aspect-[0.9] shrink-0">
                    <div
                      className="absolute bottom-2 h-[85%] w-[60%] left-[20%] dark:opacity-50"
                      style={{
                        boxShadow: `0 20px 50px 0 rgba(0, 0, 0, 0.2), 0 15px 30px 0 rgba(${getShadowColorFromBlur(
                          place.images?.[0].blurHash
                        ).join(',')},0.6)`
                      }}
                    />

                    <div className="w-full h-full nice-image rounded-xl bg-(--page-background) after:rounded-xl">
                      <Image
                        priority
                        src={getImageUrl(place.images[0]?.path, 'thumb')}
                        alt={getImageAltText(place.images[0], place.name || '', place.countryName || '')}
                        className="object-cover"
                        fill
                      />
                    </div>
                  </div>
                ) : null}

                <div className="flex flex-col justify-center gap-1 mt-[9px]">
                  <h2 className="text-xl font-semibold mr-8 leading-6">{place.name}</h2>
                  <span className="text-sm text-(--gray-text) text-left truncate w-full">
                    {formatPlaceType(place.placeType)} in {place.countryName}
                  </span>
                </div>
              </div>

              <div className="flex mt-3 gap-2 items-center">
                {userReview ? (
                  <div className="bg-(--green-secondary) rounded-md px-2">
                    <SmallRating
                      className="text-lg text-(--green-primary) mt-px"
                      starSize={12}
                      rating={userReview.mainRating}
                      starColor="var(--green-primary)"
                    />
                  </div>
                ) : null}

                {place.placeRatings?.mainRating ? (
                  <SmallRating
                    className="text-lg ml-2 mr-2 mt-px"
                    starSize={12}
                    rating={place.placeRatings?.mainRating}
                  />
                ) : null}

                <div className="grow" />

                <RatePlaceButtonWrapper place={place} key={`rate-${place?._id}`}>
                  <BaseButton buttonVariant="text" size="small">
                    <RemixIcon
                      icon={isRatedByMe ? checkLine : starFill}
                      size={18}
                      color="var(--text-color)"
                      className="-mx-[4px]"
                    />
                    <span className="ml-[12px]">{isRatedByMe ? 'Rated' : 'Rate'}</span>
                  </BaseButton>
                </RatePlaceButtonWrapper>

                <SavePlaceButtonWrapper place={place} key={`save-${place?._id}`} session={session}>
                  <BaseButton buttonVariant="text" size="small">
                    <RemixIcon icon={bucket} size={18} className="-mx-[4px] mb-[2px]" color="var(--text-color)" />
                    <span className="ml-[12px]">{isPlaceSaved ? 'Saved' : 'Save'}</span>
                  </BaseButton>
                </SavePlaceButtonWrapper>
              </div>

              <BaseButton
                buttonVariant="primary"
                className="w-full mt-3 gap-2"
                href={`/${place.countryCode}/${place.nameId}`}
              >
                Go to page
                <RemixIcon icon={arrowRightLine} size={18} color="white" />
              </BaseButton>

              <BaseButton
                onPress={unSelectPlace}
                icon={closeLine}
                buttonVariant="secondary"
                className="w-[32px] h-[32px] absolute right-4 top-4"
              />
            </motion.div>
          ) : null}

          {loadingPlace ? (
            <>
              {loadingPlace.isLoading ? (
                <div className="min-h-[164px] flex items-center justify-center">
                  <SmallLoader />
                </div>
              ) : (
                <MapPlaceCardEmptyState loadingPlace={loadingPlace} unSelectPlace={unSelectPlace} />
              )}
            </>
          ) : null}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

export default MapPlaceCard;
